import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { Row, Col, Button } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import { Badge } from "react-bootstrap";
import moment from "moment-timezone";
import {
  toBlob,
  toCanvas,
  toJpeg,
  toPixelData,
  toPng,
  toSvg,
} from "html-to-image";
import * as htmlToImage from "html-to-image";

import AdminSidebar from "../layouts/AdminSidebar";
import { authCall } from "../../services";
import MobileViewPage from "../layouts/MobileViewPage";

const COMPANYTYPE = [
  { value: "activities", label: "Booking" },
  { value: "restaurants", label: "Restaurant" },
  { value: "nightlifes", label: "Nightlife" },
];

function EmailCampaign() {
  const { current } = useSelector((state) => state.user);
  const [detailsList, setDetailsList] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [htmlGenerateLoading, setHtmlGenerateLoading] = useState(false);
  const [runLoading, setRunLoading] = useState(false);
  const [selectedCompanyType, setSelectedCompanyType] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [editingEmailId, setEditingEmailId] = useState(null);
  const [editedEmail, setEditedEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailSuccess, setEmailSuccess] = useState("");
  const [editingPhoneId, setEditingPhoneId] = useState(null);
  const [editedPhone, setEditedPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [phoneSuccess, setPhoneSuccess] = useState("");
  const [editingCompanyId, setEditingCompanyId] = useState(null);
  const [editedCompany, setEditedCompany] = useState("");
  const [companyError, setCompanyError] = useState("");
  const [companySuccess, setCompanySuccess] = useState("");
  const [checkedItems, setCheckedItems] = useState([]);
  const mobileViewRef = useRef(null);
  const [previewProps, setPreviewProps] = useState({});

  const mapItemToPreviewProps = (item) => {
    const typeMap = {
      activities: "activity",
      restaurants: "restaurant",
      nightlifes: "restaurant",
    };
    return {
      companyName: item?.name || "",
      type: typeMap[selectedCompanyType?.value] || "restaurant",
      activityName: item?.activity_name || "",
      dietaries: item?.dietaries || [],
      location: item?.address || "",
      description: item?.description || "",
      openingHours: item?.opening_hours || {},
      bannersImages: item?.images || undefined,
      mapImage: null,
    };
  };

  const waitForImages = async (container, timeout = 3000) => {
    if (!container) return;
    const imgs = Array.from(container.querySelectorAll("img"));
    const pending = imgs.filter(
      (img) => !(img.complete && img.naturalWidth > 0)
    );
    if (pending.length === 0) return;
    const loadPromises = pending.map(
      (img) =>
        new Promise((resolve) => {
          const done = () => resolve();
          img.addEventListener("load", done, { once: true });
          img.addEventListener("error", done, { once: true });
        })
    );
    await Promise.race([
      Promise.all(loadPromises),
      new Promise((r) => setTimeout(r, timeout)),
    ]);
  };

  async function getCitiesByCompanyType(type) {
    const resp = await authCall("getCitiesByCompanyType", {
      companyType: type,
    });
    if (resp?.type === "success") {
      setCities(resp?.payload);
    } else {
      setCities([]);
    }
  }

  async function fetchData(companyType, city) {
    setLoading(true);
    const resp = await authCall("getRecordsByCity", {
      companyType: companyType,
      city: city,
    });
    if (resp?.type === "success") {
      setDetailsList(resp?.payload);
      setCheckedItems([]); // Reset checked items when fetching new data
      setLoading(false);
    } else {
      setDetailsList([]);
      setCheckedItems([]);
      setLoading(false);
    }
  }

  const handleChangeCompay = (selected) => {
    setSelectedCity(selected);
    fetchData(selectedCompanyType?.value, selected?.value);
  };

  const handleChangeCompanyType = (selected) => {
    setDetailsList([]);
    setSelectedCompanyType(selected);
    setSelectedCity(null); // Reset city when company type changes
    getCitiesByCompanyType(selected?.value);
  };

  const handleEditEmail = (item, index) => {
    setEditingEmailId(index);
    setEditedEmail(item.email);
    setEmailError("");
    setEmailSuccess("");
  };

  const handleCancelEdit = () => {
    setEditingEmailId(null);
    setEditedEmail("");
    setEmailError("");
    setEmailSuccess("");
    setEditingPhoneId(null);
    setEditedPhone("");
    setPhoneError("");
    setPhoneSuccess("");
    setEditingCompanyId(null);
    setEditedCompany("");
    setCompanyError("");
    setCompanySuccess("");
  };

  const handleSaveEmail = async (item, itemIndex) => {
    setEmailError("");
    setEmailSuccess("");

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editedEmail)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    // Check if email already exists (excluding current item)
    const emailExists = detailsList.some(
      (el, idx) =>
        idx !== itemIndex &&
        el.email.toLowerCase() === editedEmail.toLowerCase()
    );

    if (emailExists) {
      setEmailError("This email already exists");
      return;
    }

    // Call API to update email
    try {
      const resp = await authCall("updateCompanyEmail", {
        userId: item.userId,
        email: editedEmail,
        companyType: selectedCompanyType?.value,
      });

      if (resp?.type === "success") {
        setEmailSuccess("Email updated successfully");
        // Update the local state
        setDetailsList(
          detailsList.map((el, idx) =>
            idx === itemIndex ? { ...el, email: editedEmail } : el
          )
        );
        setTimeout(() => {
          handleCancelEdit();
        }, 2000);
      } else {
        setEmailError(resp?.message || "Failed to update email");
      }
    } catch (error) {
      setEmailError("An error occurred while updating email");
    }
  };

  const handleEditPhone = (item, index) => {
    setEditingPhoneId(index);
    setEditedPhone(item.phone || "");
    setPhoneError("");
    setPhoneSuccess("");
  };

  const handleSavePhone = async (item, itemIndex) => {
    setPhoneError("");
    setPhoneSuccess("");

    const phoneRegex = /^[0-9+\-()\s]{6,20}$/;
    if (!editedPhone || !phoneRegex.test(editedPhone.trim())) {
      setPhoneError("Please enter a valid phone number");
      return;
    }

    try {
      const resp = await authCall("updateCompanyDetails", {
        companyId: item.id,
        phone: editedPhone.trim(),
        companyType: selectedCompanyType?.value,
      });

      if (resp?.type === "success") {
        setPhoneSuccess("Phone updated successfully");
        setDetailsList(
          detailsList.map((el, idx) =>
            idx === itemIndex ? { ...el, phone: editedPhone.trim() } : el
          )
        );
        setTimeout(() => {
          setEditingPhoneId(null);
          setEditedPhone("");
          setPhoneError("");
          setPhoneSuccess("");
        }, 2000);
      } else {
        setPhoneError(resp?.message || "Failed to update phone");
      }
    } catch (error) {
      setPhoneError("An error occurred while updating phone");
    }
  };

  const handleEditCompany = (item, index) => {
    setEditingCompanyId(index);
    setEditedCompany(item.name || "");
    setCompanyError("");
    setCompanySuccess("");
  };

  const handleSaveCompany = async (item, itemIndex) => {
    setCompanyError("");
    setCompanySuccess("");

    const value = (editedCompany || "").trim();
    if (!value || value.length < 2) {
      setCompanyError("Please enter a valid company name");
      return;
    }

    try {
      const resp = await authCall("updateCompanyDetails", {
        companyId: item.id,
        name: value,
        companyType: selectedCompanyType?.value,
      });

      if (resp?.type === "success") {
        setCompanySuccess("Company name updated successfully");
        setDetailsList(
          detailsList.map((el, idx) =>
            idx === itemIndex ? { ...el, name: value } : el
          )
        );
        setTimeout(() => {
          setEditingCompanyId(null);
          setEditedCompany("");
          setCompanyError("");
          setCompanySuccess("");
        }, 2000);
      } else {
        setCompanyError(resp?.message || "Failed to update company name");
      }
    } catch (error) {
      setCompanyError("An error occurred while updating company name");
    }
  };

  const handleCheckboxChange = (index) => {
    setCheckedItems((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setCheckedItems(detailsList.map((_, index) => index));
    } else {
      setCheckedItems([]);
    }
  };

  const isAllChecked =
    detailsList.length > 0 && checkedItems.length === detailsList.length;
  const isIndeterminate =
    checkedItems.length > 0 && checkedItems.length < detailsList.length;

  const isGenerateImageEnabled = checkedItems.length > 0;

  const isRunEnabled =
    checkedItems.length > 0 &&
    checkedItems.every((index) => detailsList[index]?.preview);

  const handleGenerateImage = async () => {
    const selectedRows = checkedItems.map((index) => detailsList[index]);
    setHtmlGenerateLoading(true);
    try {
      const generated = [];
      for (const item of selectedRows) {
        setPreviewProps(mapItemToPreviewProps(item));
        await new Promise((r) => setTimeout(r, 100));
        await waitForImages(mobileViewRef.current);

        const node = mobileViewRef.current?.querySelector("#iphone-mockup");
        if (!node) throw new Error("Preview element not found");
        const rect = node.getBoundingClientRect();
        const dataUrl = await toPng(node, {
          backgroundColor: "transparent",
          width: Math.ceil(rect.width),
          height: Math.ceil(rect.height),
          cacheBust: true,
          pixelRatio: 2,
        });
        generated.push({ userId: item.userId, image: dataUrl });
      }

      const resp = await authCall("generateHtmlPreviewForUsers", {
        items: generated,
        companyType: selectedCompanyType?.value,
      });
      if (resp?.type === "success") {
        toast.success("Images generated successfully");
        fetchData(selectedCompanyType?.value, selectedCity?.value);
      } else {
        toast.error(resp?.message || "Failed to generate images");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while generating images");
    } finally {
      setHtmlGenerateLoading(false);
    }
  };

  const handleRun = async () => {
    const selectedRows = checkedItems.map((index) => detailsList[index]);
    const leads = [];
    selectedRows.forEach((item) => {
      leads.push({
        email: item.email,
        first_name: item?.first_name,
        last_name: item?.last_name,
        company_name: item?.name,
        custom_variables: {
          ImageURL: item?.preview,
          clickURL: item?.click_url,
          CustomNote: "This is test lead",
          street: item?.street || "",
          suburb: item?.suburb || ""
        },
      });
    });

    try {
      setRunLoading(true);
      const resp = await authCall("instantly/leads", {
        leads,
      });
      if (resp?.type === "success") {
        toast.success("Campaign run successfully");
        setCheckedItems([]);
        setRunLoading(false);
      } else {
        toast.error(resp?.message || "Failed to run campaign");
        setRunLoading(false);
      }
    } catch (error) {
      toast.error("An error occurred while running campaign");
      setRunLoading(false);
    }
  };

  return (
    <>
      <div className="update-machine-page common-table-design py-4" id="phone">
        <div className="container custom-container">
          <div className="row">
            <div className="col-sm-4 col-lg-3 mb-4">
              <AdminSidebar />
            </div>
            <div className="col-sm-8 col-lg-9">
              <div className="common-info-card">
                <div className="heading-title">Email Campaign:</div>
                <div className="details-wrapper py-lg-4">
                  <Row className="align-items-center mb-4">
                    <Col xs={5}>
                      <span className="fs-sm">Search by Company Type:</span>
                      <Select
                        className="text-body mt-1"
                        placeholder="Select"
                        value={selectedCompanyType}
                        onChange={handleChangeCompanyType}
                        options={COMPANYTYPE}
                      />
                    </Col>
                    <Col xs={5}>
                      <span className="fs-sm">Search by City:</span>
                      <Select
                        className="text-body mt-1"
                        placeholder="Select"
                        value={selectedCity}
                        onChange={handleChangeCompay}
                        options={cities}
                      />
                    </Col>
                  </Row>

                  <div className="table-responsive">
                    <table className="table table-borderless table-custom m-0">
                      <thead>
                        <tr>
                          <th scope="col" width="50px">
                            <input
                              type="checkbox"
                              checked={isAllChecked}
                              ref={(el) => {
                                if (el) {
                                  el.indeterminate = isIndeterminate;
                                }
                              }}
                              style={{ height: 17 }}
                              onChange={handleSelectAll}
                            />
                          </th>
                          <th scope="col">Name:</th>
                          <th scope="col">Address:</th>
                          <th scope="col">Phone:</th>
                          <th scope="col">Email:</th>
                          <th scope="col">Status:</th>
                        </tr>
                      </thead>
                      <tbody>
                        {!loading &&
                          (detailsList.length > 0 ? (
                            detailsList.map((el, index) => (
                              <tr key={index}>
                                <td scope="row">
                                  <input
                                    style={{ height: 15 }}
                                    type="checkbox"
                                    checked={checkedItems.includes(index)}
                                    onChange={() => handleCheckboxChange(index)}
                                  />
                                </td>
                                <td>
                                  <div
                                    style={{
                                      position: "relative",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    {editingCompanyId === index ? (
                                      <div style={{ width: "100%" }}>
                                        <input
                                          type="text"
                                          className="form-control form-control-sm w-100"
                                          value={editedCompany}
                                          onChange={(e) =>
                                            setEditedCompany(e.target.value)
                                          }
                                          style={{ width: "100%" }}
                                        />
                                        <div className="mt-2 d-flex gap-2 justify-content-end">
                                          <button
                                            className="btn btn-sm btn-success"
                                            onClick={() =>
                                              handleSaveCompany(el, index)
                                            }
                                            style={{ padding: "4px 8px" }}
                                          >
                                            ✓
                                          </button>
                                          <button
                                            className="btn btn-sm btn-danger"
                                            onClick={handleCancelEdit}
                                            style={{ padding: "4px 8px" }}
                                          >
                                            ✕
                                          </button>
                                        </div>
                                        {companyError &&
                                          editingCompanyId === index && (
                                            <div
                                              className="text-danger mt-1"
                                              style={{ fontSize: "14px" }}
                                            >
                                              {companyError}
                                            </div>
                                          )}
                                        {companySuccess &&
                                          editingCompanyId === index && (
                                            <div
                                              className="text-success mt-1"
                                              style={{ fontSize: "14px" }}
                                            >
                                              {companySuccess}
                                            </div>
                                          )}
                                      </div>
                                    ) : (
                                      <>
                                        <span style={{ flex: 1 }}>{el?.name}</span>
                                        <i
                                          className="fa fa-pencil"
                                          style={{
                                            cursor: "pointer",
                                            color: "#DDB12E",
                                            marginLeft: "10px",
                                            fontSize: "14px",
                                            marginRight: "10px",
                                          }}
                                          onClick={() => handleEditCompany(el, index)}
                                          title="Edit company name"
                                        />
                                      </>
                                    )}
                                  </div>
                                </td>
                                <td>{el?.address}</td>
                                <td>
                                  <div
                                    style={{
                                      position: "relative",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    {editingPhoneId === index ? (
                                      <div style={{ width: "100%" }}>
                                        <input
                                          type="text"
                                          className="form-control form-control-sm w-100"
                                          value={editedPhone}
                                          onChange={(e) =>
                                            setEditedPhone(e.target.value)
                                          }
                                          style={{ width: "100%" }}
                                        />
                                        <div className="mt-2 d-flex gap-2 justify-content-end">
                                          <button
                                            className="btn btn-sm btn-success"
                                            onClick={() =>
                                              handleSavePhone(el, index)
                                            }
                                            style={{ padding: "4px 8px" }}
                                          >
                                            ✓
                                          </button>
                                          <button
                                            className="btn btn-sm btn-danger"
                                            onClick={handleCancelEdit}
                                            style={{ padding: "4px 8px" }}
                                          >
                                            ✕
                                          </button>
                                        </div>
                                        {phoneError &&
                                          editingPhoneId === index && (
                                            <div
                                              className="text-danger mt-1"
                                              style={{ fontSize: "14px" }}
                                            >
                                              {phoneError}
                                            </div>
                                          )}
                                        {phoneSuccess &&
                                          editingPhoneId === index && (
                                            <div
                                              className="text-success mt-1"
                                              style={{ fontSize: "14px" }}
                                            >
                                              {phoneSuccess}
                                            </div>
                                          )}
                                      </div>
                                    ) : (
                                      <>
                                        <span style={{ flex: 1 }}>{el?.phone}</span>
                                        <i
                                          className="fa fa-pencil"
                                          style={{
                                            cursor: "pointer",
                                            color: "#DDB12E",
                                            marginLeft: "10px",
                                            fontSize: "14px",
                                            marginRight: "10px",
                                          }}
                                          onClick={() => handleEditPhone(el, index)}
                                          title="Edit phone"
                                        />
                                      </>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <div
                                    style={{
                                      position: "relative",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    {editingEmailId === index ? (
                                      <div style={{ width: "100%" }}>
                                        <input
                                          type="email"
                                          className="form-control form-control-sm w-100"
                                          value={editedEmail}
                                          onChange={(e) =>
                                            setEditedEmail(e.target.value)
                                          }
                                          style={{ width: "100%" }}
                                        />
                                        <div className="mt-2 d-flex gap-2 justify-content-end">
                                          <button
                                            className="btn btn-sm btn-success"
                                            onClick={() =>
                                              handleSaveEmail(el, index)
                                            }
                                            style={{ padding: "4px 8px" }}
                                          >
                                            ✓
                                          </button>
                                          <button
                                            className="btn btn-sm btn-danger"
                                            onClick={handleCancelEdit}
                                            style={{ padding: "4px 8px" }}
                                          >
                                            ✕
                                          </button>
                                        </div>
                                        {emailError &&
                                          editingEmailId === index && (
                                            <div
                                              className="text-danger mt-1"
                                              style={{ fontSize: "14px" }}
                                            >
                                              {emailError}
                                            </div>
                                          )}
                                        {emailSuccess &&
                                          editingEmailId === index && (
                                            <div
                                              className="text-success mt-1"
                                              style={{ fontSize: "14px" }}
                                            >
                                              {emailSuccess}
                                            </div>
                                          )}
                                      </div>
                                    ) : (
                                      <>
                                        <span style={{ flex: 1 }}>
                                          {el?.email}
                                        </span>
                                        <i
                                          className="fa fa-pencil"
                                          style={{
                                            cursor: "pointer",
                                            color: "#DDB12E",
                                            marginLeft: "10px",
                                            fontSize: "14px",
                                            marginRight: "10px",
                                          }}
                                          onClick={() =>
                                            handleEditEmail(el, index)
                                          }
                                          title="Edit email"
                                        />
                                      </>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <Badge
                                    bg={
                                      el?.status_listing === "0"
                                        ? "warning"
                                        : el?.status_listing === "1"
                                        ? "primary"
                                        : "success"
                                    }
                                  >
                                    {el?.status_listing === "0"
                                      ? "Pending"
                                      : el?.status_listing === "1"
                                      ? "Sent"
                                      : "Claimed"}
                                  </Badge>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={10} className="text-center">
                                No record found
                              </td>
                            </tr>
                          ))}
                        {loading && (
                          <tr>
                            <td colSpan={10} className="text-center">
                              Loading...
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-4 text-end gap-3 d-flex justify-content-end flex-wrap">
                    <Button
                      className="btn btn-custom btn-custom-sm fs-16"
                      disabled={htmlGenerateLoading && !isGenerateImageEnabled}
                      onClick={handleGenerateImage}
                    >
                      {htmlGenerateLoading ? "Generating..." : "Generate Image"}
                    </Button>
                    <Button
                      className="btn btn-custom btn-custom-sm fs-16"
                      disabled={runLoading && !isRunEnabled}
                      onClick={handleRun}
                    >
                      {runLoading ? "Running..." : "Run"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        ref={mobileViewRef}
        style={{
          position: "absolute",
          left: "-10000px",
          top: "-10000px",
          opacity: 0,
          pointerEvents: "none",
        }}
        aria-hidden="true"
      >
        <MobileViewPage {...previewProps} />
      </div>
    </>
  );
}

export default EmailCampaign;
